import {
  AlternativeToggleRadioGroup,
  Box,
  FillIcon,
  FillIconName,
  Flex,
  FormInput,
  scaleArbitrarily,
  usePlatformProps,
} from '@acsbe/ui';
import { CSSProperties, FunctionComponent, useState } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import computerBackground from '../../../assets/Computer.png';
import mobileBackground from '../../../assets/mobile.png';
import WidgetMock from '../../../assets/widget-mock.svg';
import { notifyMixpanelClick } from '../../../services/analytics/analyticsGlobalListener';
import { CustomInstallationConfig } from '../../../validations';

export interface WidgetPreviewProps {
  control: Control<CustomInstallationConfig>;
}

export const WidgetPreview: FunctionComponent<WidgetPreviewProps> = ({
  control,
}) => {
  const p = usePlatformProps();
  const { t } = useTranslation();
  const [view, setView] = useState<'desktop' | 'mobile'>('desktop');

  const triggerPositionX = useWatch({
    control,
    name: 'triggerPositionX',
  });
  const mobileTriggerPositionX = useWatch({
    control,
    name: 'mobileTriggerPositionX',
  });

  const triggerPositionY = useWatch({
    control,
    name: 'triggerPositionY',
  });
  const mobileTriggerPositionY = useWatch({
    control,
    name: 'mobileTriggerPositionY',
  });

  const triggerOffsetX = useWatch({
    control,
    name: 'triggerOffsetX',
  });
  const mobileTriggerOffsetX = useWatch({
    control,
    name: 'mobileTriggerOffsetX',
  });

  const triggerOffsetY = useWatch({
    control,
    name: 'triggerOffsetY',
  });
  const mobileTriggerOffsetY = useWatch({
    control,
    name: 'mobileTriggerOffsetY',
  });

  const triggerColor = useWatch({
    control,
    name: 'triggerColor',
  });
  const triggerIcon = useWatch({
    control,
    name: 'triggerIcon',
  }) as FillIconName;

  const triggerRadius = useWatch({
    control,
    name: 'triggerRadius',
  });
  const mobileTriggerRadius = useWatch({
    control,
    name: 'mobileTriggerRadius',
  });

  const triggerSize = useWatch({
    control,
    name: 'triggerSize',
  });
  const mobileTriggerSize = useWatch({
    control,
    name: 'mobileTriggerSize',
  });

  const hideTrigger = useWatch({
    control,
    name: 'hideTrigger',
  });
  const hideMobile = useWatch({
    control,
    name: 'hideMobile',
  });
  const interfaceColor = useWatch({
    control,
    name: 'leadColor',
  });

  const triggerScale =
    triggerSize === 'medium' ? 28 : triggerSize === 'small' ? 20 : 34;

  const mobileTriggerScale =
    mobileTriggerSize === 'medium'
      ? 28
      : mobileTriggerSize === 'small'
        ? 20
        : 34;

  const triggerBlockProperties = calcTriggerBlockPosition(
    triggerPositionY,
    triggerOffsetY,
    '45%',
  );
  const mobileTriggerBlockProperties = calcTriggerBlockPosition(
    mobileTriggerPositionY,
    mobileTriggerOffsetY,
    '40%',
  );

  const triggerInlineProperties = calcTriggerInlinePosition(
    triggerScale,
    triggerPositionX,
    triggerOffsetX,
  );
  const mobileTriggerInlineProperties = calcTriggerInlinePosition(
    mobileTriggerScale,
    mobileTriggerPositionX,
    mobileTriggerOffsetX,
  );

  const triggerStyle =
    view === 'desktop'
      ? ({
          ['--fill' as 'backgroundColor']: triggerColor,
          ['--text' as 'color']: 'white',
          ['--size' as 'width']: `${triggerScale}px`,
          borderRadius: triggerRadius,
          visibility: hideTrigger === 'true' ? 'hidden' : 'visible',
          ...triggerBlockProperties,
          ...triggerInlineProperties,
        } as const)
      : ({
          ['--fill' as 'backgroundColor']: triggerColor,
          ['--text' as 'color']: 'white',
          ['--size' as 'width']: `${mobileTriggerScale}px`,
          borderRadius: mobileTriggerRadius,
          visibility: hideMobile === 'true' ? 'hidden' : 'visible',
          ...mobileTriggerBlockProperties,
          ...mobileTriggerInlineProperties,
        } as const);

  return (
    <PreviewPanel
      {...p.fromDesktopSm({
        self: 'start',
        maxWidth: '400px',
      })}
      gap={6}
      position="sticky"
      role="presentation"
    >
      <AlternativeToggleRadioGroup
        label={t('VIEWPORT_TYPE')}
        name="view"
        onChange={(view) => setView(view as 'desktop' | 'mobile')}
        stretch
        value={view}
      >
        <FormInput<'radio'>
          data-mixpanel="desktop-widget-preview-installation"
          label={t('DESKTOP')}
          onClick={(e) => notifyMixpanelClick(e.target as HTMLInputElement)}
          type="radio"
          value="desktop"
        />
        <FormInput<'radio'>
          data-mixpanel="mobile-widget-preview-installation"
          label={t('MOBILE')}
          onClick={(e) => notifyMixpanelClick(e.target as HTMLInputElement)}
          type="radio"
          value="mobile"
        />
      </AlternativeToggleRadioGroup>
      <FrameContainer>
        <TriggerBackgroundContainer
          width={view === 'desktop' ? '272px' : '132px'}
          height={view === 'desktop' ? '138px' : '200px'}
        >
          <img
            src={view === 'desktop' ? computerBackground : mobileBackground}
            alt={view}
          />
          <TriggerContainer isDesktop={view === 'desktop'}>
            <Trigger style={triggerStyle}>
              <FillIcon name={triggerIcon} size="xl" />
            </Trigger>
          </TriggerContainer>
        </TriggerBackgroundContainer>
      </FrameContainer>
      <FrameContainer>
        <StyledWidgetMock
          //@ts-ignore
          style={{ '--fill': interfaceColor }}
        />
      </FrameContainer>
    </PreviewPanel>
  );
};

const Trigger = styled(Flex).attrs({
  align: 'center',
  justify: 'center',
  position: 'absolute',
})`
  transition:
    background-color 0.1s linear,
    scale 0.1s linear;
  cursor: pointer;
  width: var(--size);
  height: var(--size);

  &:hover:not(:active) {
    scale: 1.1;
  }

  i {
    align-items: center;
    justify-content: center;
    width: 94%;
    height: 94%;
  }
`;

const PreviewPanel = styled(Flex)`
  inset-block-start: ${scaleArbitrarily(21)};
`;

const TriggerContainer = styled.div<{ isDesktop?: boolean }>`
  position: absolute;
  top: ${(props) => (props.isDesktop ? '21px' : '2px')};
  left: 2px;
  width: calc(100% - 4px);
  height: calc(100% - ${(props) => (props.isDesktop ? '22px' : '4px')});
`;

const TriggerBackgroundContainer = styled(Box)`
  position: relative;
  text-align: center;
`;

const FrameContainer = styled(Flex)`
  height: 100%;
  position: relative;
  min-width: 272px;
  align-items: center;
  padding: 24px;
  background: white;
  border-radius: 12px;
  border: 1px solid #cbd2e1;
`;

const StyledWidgetMock = styled(WidgetMock)`
  [fill] {
    transition: fill 0.1s linear;
  }
`;

const calcTriggerBlockPosition = (
  positionY: string,
  offsetY: string,
  centerY: string,
): CSSProperties => {
  const offset = `${parseInt(offsetY)}px`;

  return positionY === 'bottom'
    ? {
        insetBlockEnd: offset,
      }
    : positionY === 'top'
      ? {
          insetBlockStart: offset,
        }
      : {
          insetBlockStart: `calc(${centerY} - ${offset})`,
        };
};

const calcTriggerInlinePosition = (
  scale: number,
  positionX: string,
  offsetX: string,
): CSSProperties => {
  const offset = `${parseInt(offsetX)}px`;

  return positionX === 'right'
    ? {
        insetInlineEnd: offset,
      }
    : {
        insetInlineStart: offset,
      };
};
